import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { RecurringPaymentRuleUseCases } from "@/application/use-cases/RecurringPaymentRuleUseCases";
import { logger } from "@/infrastructure/services/LoggerService";
import { useToast } from "./useToast";
/**
 * Composable for managing recurring payment rules (subscriptions)
 * Provides reactive state and operations for the subscriptions dashboard
 */
export function useSubscriptions() {
    // Dependencies
    const useCases = new RecurringPaymentRuleUseCases();
    const { showSuccess, showError } = useToast();
    const { t } = useI18n();
    // State
    const listItems = ref([]);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const totalCount = ref(0);
    const currentSearchTerm = ref(null);
    const currentFilters = ref({});
    const isLoading = ref(false);
    const error = ref(null);
    const filterCounts = ref(null);
    // Computed
    const hasItems = computed(() => listItems.value.length > 0);
    const isSearchActive = computed(() => currentSearchTerm.value !== null && currentSearchTerm.value.length > 0);
    const hasNextPage = computed(() => currentPage.value * pageSize.value < totalCount.value);
    const hasPreviousPage = computed(() => currentPage.value > 1);
    const totalPages = computed(() => {
        if (pageSize.value <= 0)
            return 0;
        return Math.ceil(totalCount.value / pageSize.value);
    });
    /**
     * Load a paginated list of recurring payment rules
     */
    const loadList = async (page = 1, size = 10, searchTerm, filters) => {
        try {
            isLoading.value = true;
            error.value = null;
            // Persist the search term if provided, or clear it if explicitly empty
            if (searchTerm !== undefined) {
                currentSearchTerm.value = searchTerm || null;
            }
            // Persist filters if provided
            if (filters !== undefined) {
                currentFilters.value = filters;
            }
            logger.debug("Loading recurring payment rules list", {
                page,
                size,
                searchTerm: currentSearchTerm.value,
                filters: currentFilters.value,
            });
            const response = await useCases.getList(page, size, currentSearchTerm.value || undefined, Object.keys(currentFilters.value).length > 0 ? currentFilters.value : undefined);
            listItems.value = response.items;
            currentPage.value = response.page;
            pageSize.value = response.pageSize;
            totalCount.value = response.totalCount;
            // Refresh filter counts in the background (non-blocking)
            loadFilterCounts();
            logger.debug("Recurring payment rules list loaded successfully", {
                totalCount: response.totalCount,
                itemCount: response.items.length,
            });
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to load subscriptions";
            error.value = errorMessage;
            showError(errorMessage);
            logger.debug("Failed to load recurring payment rules list", err);
        }
        finally {
            isLoading.value = false;
        }
    };
    /**
     * Load faceted filter counts for current filters.
     * Counts are contextual: status counts exclude the status filter, frequency counts exclude the frequency filter.
     */
    const loadFilterCounts = async (filters) => {
        try {
            const filtersToUse = filters ?? currentFilters.value;
            // Strip sort params — they don't affect counts
            const { sortBy: _sortBy, sortOrder: _sortOrder, ...countFilters } = filtersToUse;
            filterCounts.value = await useCases.getFilterCounts(Object.keys(countFilters).length > 0 ? countFilters : undefined);
        }
        catch {
            // Filter counts are non-critical — don't block the UI
            logger.debug("Failed to load filter counts (non-critical)");
        }
    };
    /**
     * Search recurring payment rules by email or reference
     */
    const search = async (term) => {
        // Search now uses the same list endpoint with a search term
        await loadList(1, pageSize.value, term);
    };
    /**
     * Cancel a recurring payment rule (subscription)
     */
    const cancel = async (id, cancellationReason) => {
        try {
            isLoading.value = true;
            error.value = null;
            logger.debug("Cancelling recurring payment rule", { id, cancellationReason });
            await useCases.cancel(id, cancellationReason);
            showSuccess(t("dashboard.subscriptions.feedback.success.cancelSuccess"));
            logger.debug("Recurring payment rule cancelled successfully", { id });
            // Reload the current view with the same search term
            await loadList(currentPage.value, pageSize.value);
            return true;
        }
        catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : t("dashboard.subscriptions.feedback.error.cancelFailed");
            error.value = errorMessage;
            showError(errorMessage);
            logger.debug("Failed to cancel recurring payment rule", err);
            return false;
        }
        finally {
            isLoading.value = false;
        }
    };
    /**
     * Pause an active recurring payment rule (temporarily stops future transactions)
     */
    const pause = async (id) => {
        try {
            isLoading.value = true;
            error.value = null;
            logger.debug("Pausing recurring payment rule", { id });
            await useCases.pause(id);
            showSuccess(t("dashboard.subscriptions.feedback.success.pauseSuccess"));
            logger.debug("Recurring payment rule paused successfully", { id });
            // Reload the current view with the same search term
            await loadList(currentPage.value, pageSize.value);
            return true;
        }
        catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : t("dashboard.subscriptions.feedback.error.pauseFailed");
            error.value = errorMessage;
            showError(errorMessage);
            logger.debug("Failed to pause recurring payment rule", err);
            return false;
        }
        finally {
            isLoading.value = false;
        }
    };
    /**
     * Resume a paused recurring payment rule (restarts future transactions)
     */
    const resume = async (id) => {
        try {
            isLoading.value = true;
            error.value = null;
            logger.debug("Resuming recurring payment rule", { id });
            await useCases.resume(id);
            showSuccess(t("dashboard.subscriptions.feedback.success.resumeSuccess"));
            logger.debug("Recurring payment rule resumed successfully", { id });
            // Reload the current view with the same search term
            await loadList(currentPage.value, pageSize.value);
            return true;
        }
        catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : t("dashboard.subscriptions.feedback.error.resumeFailed");
            error.value = errorMessage;
            showError(errorMessage);
            logger.debug("Failed to resume recurring payment rule", err);
            return false;
        }
        finally {
            isLoading.value = false;
        }
    };
    /**
     * Cancel only the next scheduled payment
     */
    const cancelNext = async (id) => {
        try {
            isLoading.value = true;
            error.value = null;
            logger.debug("Cancelling next payment", { id });
            await useCases.cancelNext(id);
            showSuccess(t("dashboard.subscriptions.feedback.success.cancelNextSuccess"));
            logger.debug("Next payment cancelled successfully", { id });
            // Reload the current view with the same search term
            await loadList(currentPage.value, pageSize.value);
            return true;
        }
        catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : t("dashboard.subscriptions.feedback.error.cancelNextFailed");
            error.value = errorMessage;
            showError(errorMessage);
            logger.debug("Failed to cancel next payment", err);
            return false;
        }
        finally {
            isLoading.value = false;
        }
    };
    /**
     * Update a recurring payment rule configuration
     */
    const update = async (id, updateData) => {
        try {
            isLoading.value = true;
            error.value = null;
            logger.debug("Updating recurring payment rule", { id, updateData });
            const request = {
                id,
                amount: updateData.amount,
                taxAmount: updateData.taxAmount,
                frequency: updateData.frequency,
                transactionCount: updateData.transactionCount,
            };
            await useCases.update(request);
            showSuccess(t("dashboard.subscriptions.feedback.success.updateSuccess"));
            logger.debug("Recurring payment rule updated successfully", { id });
            // Reload the current view with the same search term
            await loadList(currentPage.value, pageSize.value);
            return true;
        }
        catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : t("dashboard.subscriptions.feedback.error.updateFailed");
            error.value = errorMessage;
            showError(errorMessage);
            logger.debug("Failed to update recurring payment rule", err);
            return false;
        }
        finally {
            isLoading.value = false;
        }
    };
    /**
     * Navigate to next page
     */
    const nextPage = async () => {
        if (hasNextPage.value) {
            await loadList(currentPage.value + 1, pageSize.value);
        }
    };
    /**
     * Navigate to previous page
     */
    const previousPage = async () => {
        if (hasPreviousPage.value) {
            await loadList(currentPage.value - 1, pageSize.value);
        }
    };
    /**
     * Go to specific page
     */
    const goToPage = async (page) => {
        if (page >= 1 && page <= totalPages.value) {
            await loadList(page, pageSize.value);
        }
    };
    /**
     * Clear search results and return to list view
     */
    const clearSearch = async () => {
        currentSearchTerm.value = null;
        await loadList(1, pageSize.value);
    };
    /**
     * Apply filters and reload the list from page 1
     */
    const applyFilters = async (filters) => {
        currentFilters.value = filters;
        await loadList(1, pageSize.value, currentSearchTerm.value || undefined, filters);
    };
    /**
     * Clear error state
     */
    const clearError = () => {
        error.value = null;
    };
    return {
        // State
        listItems,
        currentPage,
        pageSize,
        totalCount,
        currentSearchTerm,
        isLoading,
        error,
        filterCounts,
        // Computed
        hasItems,
        isSearchActive,
        hasNextPage,
        hasPreviousPage,
        totalPages,
        // Actions
        loadList,
        search,
        cancel,
        pause,
        resume,
        cancelNext,
        update,
        nextPage,
        previousPage,
        goToPage,
        clearSearch,
        applyFilters,
        clearError,
    };
}
